import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { getForumPost, getPostComments, createComment, toggleLike } from '@/lib/forum';
import type { ForumPost, ForumComment } from '@/lib/types/forum';
import { MessageSquare, ThumbsUp, Send, ArrowLeft, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function Post() {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [post, setPost] = useState<ForumPost | null>(null);
  const [comments, setComments] = useState<ForumComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commenting, setCommenting] = useState(false);
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        if (!id) return;
        
        const [postData, commentsData] = await Promise.all([
          getForumPost(id),
          getPostComments(id)
        ]);
        
        setPost(postData);
        setComments(commentsData);
      } catch (error) {
        console.error('Error loading post:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de charger la discussion.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [id, toast]);

  const handleComment = async () => {
    if (!currentUser || !post || !newComment.trim()) return;

    try {
      setCommenting(true);
      const comment = await createComment({
        content: newComment,
        postId: post.id,
        userId: currentUser.id
      });

      setComments(prev => [...prev, comment]);
      setNewComment('');
      
      toast({
        title: "Commentaire ajouté",
        description: "Votre commentaire a été publié avec succès.",
      });
    } catch (error) {
      console.error('Error creating comment:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'ajouter le commentaire.",
      });
    } finally {
      setCommenting(false);
    }
  };

  const handleLike = async () => {
    if (!currentUser || !post) return;

    try {
      const isLiked = await toggleLike(currentUser.id, post.id);
      setPost(prev => prev ? {
        ...prev,
        likesCount: prev.likesCount + (isLiked ? 1 : -1)
      } : null);
    } catch (error) {
      console.error('Error toggling like:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de liker la discussion.",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Discussion introuvable</h1>
          <Button asChild>
            <Link to="/forum">Retour au forum</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Post */}
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader className="flex flex-col space-y-4">
            <div className="flex items-center justify-between">
              <Link
                to={`/forum/category/${post.category?.slug}`}
                className="text-[#54075B] hover:underline flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour à {post.category?.name}
              </Link>
              {currentUser && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLike}
                  className="flex items-center gap-2"
                >
                  <ThumbsUp className="w-4 h-4" />
                  {post.likesCount}
                </Button>
              )}
            </div>
            <h1 className="text-2xl font-bold">{post.title}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>Par {post.user?.displayName}</span>
              <span>{format(new Date(post.createdAt), 'PPP', { locale: fr })}</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              {post.content.split('\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Comments */}
        <Card className="backdrop-blur-sm bg-white/90">
          <CardHeader>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Commentaires ({comments.length})
            </h2>
          </CardHeader>
          <CardContent className="space-y-6">
            {currentUser && (
              <div className="space-y-4">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Ajouter un commentaire..."
                  className="min-h-[100px]"
                />
                <Button
                  onClick={handleComment}
                  disabled={commenting || !newComment.trim()}
                  className="flex items-center gap-2"
                >
                  {commenting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Commenter
                </Button>
              </div>
            )}

            <div className="space-y-4">
              {comments.map((comment) => (
                <Card key={comment.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">
                            {comment.user?.displayName}
                          </span>
                          <span className="text-sm text-gray-500">
                            {format(new Date(comment.createdAt), 'PPP', { locale: fr })}
                          </span>
                        </div>
                        <p className="text-gray-700">
                          {comment.content}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {comments.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>Aucun commentaire pour le moment.</p>
                  {currentUser && (
                    <p className="mt-2">Soyez le premier à commenter !</p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}